import { useEffectOnce } from '@ardoq/hooks';
import { useRef, useState } from 'react';
import { ModalTemplate } from '../components/ModalTemplate';
import { ModalSize } from '../types';
import { ConfirmWithYesInputDialogProps } from './types';

export const ConfirmWithYesInputDialog = ({
  title,
  errorMessage,
  body,
  rememberActionCustomCheckboxText,
  showRememberActionCheckbox,
  confirmButtonTitle = 'Ok',
  confirmButtonClickId,
  cancelButtonTitle = 'Cancel',
  cancelButtonClickId,
  isConfirmButtonDanger,
  modalSize = ModalSize.S,
  onConfirm,
  onCancel,
  inputDataTestId,
  yesInputLabel = 'Type "yes" to continue',
  ...restOfDialogBaseProps
}: ConfirmWithYesInputDialogProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [rememberAction, setRememberAction] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const isConfirmButtonDisabled = inputValue.toLowerCase() !== 'yes';
  const onConfirmProp = isConfirmButtonDisabled
    ? undefined
    : () =>
        onConfirm({
          ...(showRememberActionCheckbox ? { rememberAction } : {}),
          confirmed: true,
        });

  useEffectOnce(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });

  return (
    <ModalTemplate
      modalSize={modalSize}
      headerText={title}
      inputLabel={yesInputLabel}
      onInputValueChange={setInputValue}
      inputValue={inputValue}
      onInputEnterPress={onConfirmProp}
      textInputRef={inputRef}
      errorBlockMessage={errorMessage}
      rememberActionCheckboxState={rememberAction}
      onRememberActionCheckboxChange={setRememberAction}
      rememberActionCustomCheckboxText={
        rememberActionCustomCheckboxText || 'Don’t ask me again'
      }
      showRememberActionCheckbox={showRememberActionCheckbox}
      primaryButtonText={!isConfirmButtonDanger ? confirmButtonTitle : ''}
      primaryButtonClickId={confirmButtonClickId}
      onPrimaryButtonClick={onConfirmProp}
      isPrimaryButtonDisabled={isConfirmButtonDisabled}
      dangerButtonText={isConfirmButtonDanger ? confirmButtonTitle : ''}
      dangerButtonClickId={confirmButtonClickId}
      onDangerButtonClick={onConfirmProp}
      isDangerButtonDisabled={isConfirmButtonDisabled}
      secondaryButtonText={cancelButtonTitle}
      secondaryButtonClickId={cancelButtonClickId}
      onSecondaryButtonClick={onCancel}
      inputDataTestId={inputDataTestId}
      {...restOfDialogBaseProps}
    >
      {body}
    </ModalTemplate>
  );
};

export const ConfirmDeleteWithYesInputDialog = ({
  isConfirmButtonDanger = true,
  confirmButtonTitle = 'Delete',
  inputDataTestId = 'confirm-delete-with-yes-input',
  ...config
}: ConfirmWithYesInputDialogProps) => (
  <ConfirmWithYesInputDialog
    {...config}
    isConfirmButtonDanger={isConfirmButtonDanger}
    confirmButtonTitle={confirmButtonTitle}
    inputDataTestId={inputDataTestId}
  />
);
